import { useEffect, useState } from "react";
import classes from "./ExplanationModal.module.css";

const ExplanationModal = ({ isOpen, onClose, explanation }) => {
  const [showRawData, setShowRawData] = useState(false);

  // Handle escape key press
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !explanation) {
    return null;
  }

  // Parse the explanation to extract field contributions
  const parseExplanation = (explanation) => {
    const fields = [];

    // Helper function to recursively extract field scores
    const extractFields = (node, path = []) => {
      if (!node) return;

      if (node.description) {
        const desc = node.description;

        // Check for location/distance function
        if (desc.includes("Function for field location")) {
          fields.push({
            field: "location (distance)",
            score: node.value || 0,
            description: desc,
            isLocation: true,
          });
        }

        // Check for weight patterns like "weight(description:cafe in 3714)"
        const weightMatch = desc.match(/weight\(([^:]+):([^ ]+)[^)]+\)/);
        if (weightMatch) {
          const fieldName = weightMatch[1];
          const matchedValue = weightMatch[2];

          // Look for boost value in the details
          let boostValue = null;
          if (node.details && Array.isArray(node.details)) {
            const boostNode = node.details.find(
              (detail) => detail.description && detail.description.includes("boost * idf * tf")
            );
            if (boostNode && boostNode.details) {
              const boostDetail = boostNode.details.find((detail) => detail.description === "boost");
              if (boostDetail) {
                boostValue = boostDetail.value;
              }
            }
          }

          const boostValueStr = boostValue ? ` (boost: ${boostValue})` : "";
          const displayName = `${fieldName} "${matchedValue}"${boostValueStr}`;

          fields.push({
            field: displayName,
            score: node.value || 0,
            description: desc,
            isLocation: false,
            boost: boostValue,
          });
        }
      }

      // Recursively check details array
      if (node.details && Array.isArray(node.details)) {
        node.details.forEach((detail) => extractFields(detail, [...path, "details"]));
      }
    };

    extractFields(explanation);

    // Sort so location comes first, then by score descending
    return fields.sort((a, b) => {
      if (a.isLocation && !b.isLocation) return -1;
      if (!a.isLocation && b.isLocation) return 1;
      return b.score - a.score;
    });
  };

  const parsedFields = parseExplanation(explanation);

  // Separate scaling for location vs field scores
  const regularFields = parsedFields.filter((f) => !f.isLocation);
  const maxRegularScore = Math.max(...regularFields.map((f) => f.score), 1);

  const getBarWidth = (field) => {
    if (field.isLocation) {
      // For location: value of 1 = 50% width, scale proportionally
      return Math.min((field.score / 1) * 50, 100);
    } else {
      // For regular fields: use relative scaling
      return (field.score / maxRegularScore) * 100;
    }
  };

  // Handle clicking outside the modal
  const handleOverlayClick = (e) => {
    // Only close if clicking directly on the overlay, not on its children
    if (e.target === e.currentTarget) {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  return (
    <div className={classes.modalOverlay} onClick={handleOverlayClick}>
      <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={classes.modalHeader}>
          <h3>Search Score Explanation</h3>
          <button className={classes.closeButton} onClick={onClose}>
            ×
          </button>
        </div>
        <div className={classes.modalBody}>
          {/* Parsed Visual Representation */}
          <div className={classes.parsedSection}>
            <h4>Field Contributions</h4>
            {parsedFields.length > 0 ? (
              <div className={classes.fieldsList}>
                {parsedFields.map((field, index) => (
                  <div key={index} className={classes.fieldRow}>
                    <div className={classes.fieldInfo}>
                      <span className={classes.fieldName}>{field.field}</span>
                      <span className={classes.fieldScore}>{field.score.toFixed(3)}</span>
                    </div>
                    <div className={classes.scoreBarContainer}>
                      <div
                        className={`${classes.scoreBar} ${field.isLocation ? classes.locationBar : classes.fieldBar}`}
                        style={{
                          width: `${getBarWidth(field)}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className={classes.noFields}>No field contributions found</p>
            )}
          </div>

          {/* Raw JSON - Collapsible */}
          <div className={classes.rawSection}>
            <button className={classes.toggleButton} onClick={() => setShowRawData(!showRawData)}>
              <span>Raw Explanation Data</span>
              <span className={`${classes.toggleIcon} ${showRawData ? classes.expanded : ""}`}>▼</span>
            </button>
            {showRawData && <pre className={classes.explanationText}>{JSON.stringify(explanation, null, 2)}</pre>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExplanationModal;
